/**
 * O estado do vínculo de uma sugestão com o roteiro, e as ações de cada um
 * (PO, 22/09/2026): o autor pede, quem administra decide — e faz direto.
 *
 * Tudo o que aparece aqui vem do servidor (`linkStatus` e os `can*`): a tela
 * nunca infere permissão. Sem rede, as ações ficam desabilitadas.
 */
import { useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { type Sugestao, useVinculoDaSugestao } from './api';
import { EscolherAtividadeSheet } from './EscolherAtividadeSheet';

const diaCurto = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** No chip cabe o primeiro nome; a lista já diz quem é por extenso. */
const primeiroNome = (nome: string) => nome.trim().split(/\s+/)[0] ?? nome;

export function VinculoDaSugestao({ s, online = true }: { s: Sugestao; online?: boolean }) {
  const { tripId } = useTrip();
  const acoes = useVinculoDaSugestao(tripId);
  const [escolhendo, setEscolhendo] = useState(false);
  const ocupado = acoes.vincular.isPending || acoes.desvincular.isPending || acoes.cancelar.isPending || acoes.decidir.isPending;
  const erro = (acoes.vincular.error ?? acoes.desvincular.error ?? acoes.cancelar.error ?? acoes.decidir.error) as Error | null;

  const chip =
    s.linkStatus === 'linked' && s.activity ? (
      <span className="sug__vinculo-chip sug__vinculo-chip--ok" data-testid="sug-vinculo-chip">
        <Icon name="roteiro" size={13} /> No roteiro · {s.activity.title} ({diaCurto(s.activity.date)})
      </span>
    ) : s.linkStatus === 'pending_link' ? (
      <span className="sug__vinculo-chip" data-testid="sug-vinculo-chip">
        ⏳ {s.canDecide ? `${primeiroNome(s.requestedBy?.name ?? 'Alguém')} quer pôr em ${s.requestedActivity?.title ?? 'uma atividade'}` : `Aguardando aprovação${s.requestedActivity ? ` · ${s.requestedActivity.title}` : ''}`}
      </span>
    ) : s.linkStatus === 'pending_unlink' ? (
      <span className="sug__vinculo-chip" data-testid="sug-vinculo-chip">
        ⏳ {s.canDecide ? `${primeiroNome(s.requestedBy?.name ?? 'Alguém')} quer tirar do roteiro` : 'Aguardando aprovação da retirada'}
      </span>
    ) : s.linkStatus === 'rejected' ? (
      <span className="sug__vinculo-chip sug__vinculo-chip--nao" data-testid="sug-vinculo-chip">Não aprovado</span>
    ) : null;

  return (
    <div className="sug__vinculo">
      {chip}
      <div className="sug__vinculo-acoes">
        {s.canDecide && (
          <>
            <button type="button" className="nv-btn nv-btn--chip" disabled={ocupado || !online} onClick={() => acoes.decidir.mutate({ id: s.id, approve: false })} data-testid="sug-recusar">
              Recusar
            </button>
            <button type="button" className="nv-btn nv-btn--chip nv-btn--primary" disabled={ocupado || !online} onClick={() => acoes.decidir.mutate({ id: s.id, approve: true })} data-testid="sug-aprovar">
              Aprovar
            </button>
          </>
        )}
        {s.canCancelRequest && (
          <button type="button" className="nv-btn nv-btn--chip" disabled={ocupado || !online} onClick={() => acoes.cancelar.mutate(s.id)} data-testid="sug-cancelar-pedido">
            Cancelar pedido
          </button>
        )}
        {s.canRequestLink && (
          <button type="button" className="nv-btn nv-btn--chip" disabled={ocupado || !online} onClick={() => setEscolhendo(true)} data-testid="sug-vincular">
            Pôr no roteiro
          </button>
        )}
        {s.canRequestUnlink && (
          <button type="button" className="nv-btn nv-btn--chip" disabled={ocupado || !online} onClick={() => acoes.desvincular.mutate(s.id)} data-testid="sug-desvincular">
            Tirar do roteiro
          </button>
        )}
      </div>
      {!online && (s.canRequestLink || s.canDecide) && <p className="auth-note">Precisa de internet para mexer no roteiro.</p>}
      {erro && <p className="auth-error" role="alert">{erro.message}</p>}
      {escolhendo && (
        <EscolherAtividadeSheet
          titulo="Em qual atividade?"
          ocupado={ocupado}
          onEscolher={(activityId) => {
            acoes.vincular.mutate({ id: s.id, activityId });
            setEscolhendo(false);
          }}
          onFechar={() => setEscolhendo(false)}
        />
      )}
    </div>
  );
}
